// frontend/src/pages/admin/agents.jsx
// Updated: April 7, 2025
import React, { useState, useEffect } from 'react';
import { getDatabaseAgents, createAgent, updateAgent, deleteAgent } from 'api';
import AgentList from '../../components/AgentList';
import AgentForm from '../../components/AgentForm';
import BulkAgentUpload from '../../components/BulkAgentUpload';

const AgentsAdmin = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentAgent, setCurrentAgent] = useState(null);
  const [formMode, setFormMode] = useState('create');
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'create', or 'bulk'
  const [retryCount, setRetryCount] = useState(0);
  const [retryTimer, setRetryTimer] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);

  // Debounce function to prevent rapid API calls
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  const fetchAgents = async () => {
    // Prevent duplicate requests within 2 seconds
    const now = Date.now();
    if (now - lastFetchTime < 2000) {
      console.log('Skipping duplicate request, last fetch was too recent');
      return;
    }
    setLastFetchTime(now);
    
    setLoading(true);
    try {
      console.log('Fetching agents from Railway database...');
      const result = await getDatabaseAgents();
      console.log('Agents API response:', result);
      
      if (result.success) {
        // Add debugging to see what result.data actually is
        console.log('Result data type:', typeof result.data);
        console.log('Is array?', Array.isArray(result.data));
        console.log('Result data:', result.data);
        
        // Check if result.data is an array before mapping
        if (Array.isArray(result.data)) {
          // Ensure each agent has selectedStates array
          const formattedAgents = result.data.map(agent => ({
            ...agent,
            selectedStates: agent.selectedStates || []
          }));
          setAgents(formattedAgents);
          setError(''); // Clear any previous errors
          setRetryCount(0); // Reset retry count on success
        } else if (result.data && typeof result.data === 'object') {
          // Handle case where data might be an object instead of array
          console.log('Data is an object, not an array. Checking for data property...');
          
          // Try to extract agents array from nested data property if it exists
          const agentsArray = result.data.data || result.data.agents || [];
          
          if (Array.isArray(agentsArray)) {
            const formattedAgents = agentsArray.map(agent => ({
              ...agent,
              selectedStates: agent.selectedStates || []
            }));
            setAgents(formattedAgents);
            setError('');
            setRetryCount(0);
          } else {
            console.error('Could not extract agents array from response:', result);
            setError('Invalid response format from server');
          }
        } else {
          console.error('Invalid response data format:', result.data);
          setError('Invalid data format received from server');
        }
      }
      else {
        console.error('Failed to load agents:', result);
        
        // Handle rate limiting
        if (result.status === 429 && result.retryAfter) {
          setError(`Rate limit exceeded. System will retry in ${result.retryAfter} seconds...`);
          
          // Clear existing timer
          if (retryTimer) {
            clearTimeout(retryTimer);
          }
          
          // Set up retry timer
          const timer = setTimeout(() => {
            console.log('Retrying agent fetch after rate limit cooldown');
            fetchAgents(); // Retry the request
          }, result.retryAfter * 1000);
          
          setRetryTimer(timer);
        } else {
          setError(result.message || 'Failed to fetch agents');
        }
      }
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('An unexpected error occurred while fetching agents');
    } finally {
      setLoading(false);
    }
  };

  // Debounced version of fetchAgents to prevent rapid API calls
  const debouncedFetchAgents = debounce(fetchAgents, 500);

  useEffect(() => {
    // Initial fetch
    debouncedFetchAgents();
    
    // Cleanup function to clear any pending timers
    return () => {
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
  }, []);

  // Handle retry timer changes
  useEffect(() => {
    if (retryTimer) {
      return () => clearTimeout(retryTimer);
    }
  }, [retryTimer]);

  const handleCreateAgent = () => {
    setCurrentAgent(null);
    setFormMode('create');
    setActiveTab('create');
  };

  const handleEditAgent = (agent) => {
    setCurrentAgent(agent);
    setFormMode('edit');
    setActiveTab('create');
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const result = await deleteAgent(agentId);
      if (result.success) setAgents(agents.filter(agent => agent.id !== agentId));
      else setError(result.message);
    } catch (err) {
      setError('Failed to delete agent.');
    }
  };

  const handleFormSubmit = async (agentData) => {
    try {
      // Make sure agent data has selectedStates property even if not selected
      const formattedData = {
        ...agentData,
        selectedStates: agentData.selectedStates || []
      };
      
      const result = formMode === 'create' 
        ? await createAgent(formattedData) 
        : await updateAgent(currentAgent.id, formattedData);
        
      if (result.success) {
        // Use the debounced fetch to avoid duplicate requests
        debouncedFetchAgents();
        setActiveTab('list');
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(`Failed to ${formMode} agent.`);
    }
  };

  const handleCancelForm = () => {
    setActiveTab('list');
  };

  // Refresh agent list (for after bulk upload)
  const refreshAgentList = async () => {
    // Use the debounced fetch to avoid duplicate requests
    debouncedFetchAgents();
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex justify-between mb-4">
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">Manage Agents</h1>
        <div className="flex space-x-2">
          <button 
            onClick={() => setActiveTab('list')}
            className={`btn ${activeTab === 'list' ? 'btn-primary' : 'btn-secondary'} text-sm sm:text-base`}
          >
            View Agents
          </button>
          <button 
            onClick={handleCreateAgent} 
            className={`btn ${activeTab === 'create' ? 'btn-primary' : 'btn-secondary'} text-sm sm:text-base`}
          >
            Add New Agent
          </button>
          <button 
            onClick={() => setActiveTab('bulk')}
            className={`btn ${activeTab === 'bulk' ? 'btn-primary' : 'btn-secondary'} text-sm sm:text-base`}
          >
            Bulk Upload
          </button>
        </div>
      </div>
      
      {error && <div className="error-alert mb-4">{error}</div>}
      
      {activeTab === 'create' && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-4">
          <h2 className="text-lg font-medium mb-4">{formMode === 'create' ? 'Add New Agent' : 'Edit Agent'}</h2>
          <AgentForm
            agent={currentAgent}
            onSubmit={handleFormSubmit}
            submitLabel={formMode === 'create' ? 'Create Agent' : 'Update Agent'}
          />
          <button onClick={handleCancelForm} className="btn btn-secondary mt-2">Cancel</button>
        </div>
      )}
      
      {activeTab === 'bulk' && (
        <div className="mb-4">
          <BulkAgentUpload onComplete={refreshAgentList} />
        </div>
      )}
      
      {activeTab === 'list' && (
        loading ? (
          <div className="text-center text-sm sm:text-base">Loading...</div>
        ) : (
          <AgentList agents={agents} onEdit={handleEditAgent} onDelete={handleDeleteAgent} />
        )
      )}
    </div>
  );
};

export default AgentsAdmin;